// In-world 2D drawing: speech and thought bubbles, the dialogue box, name tags. Ported from the single-player town.
import { EMOTES, EMOTE_COLORS, INK, inEll, sprite } from '../../legacy/art.gen.js';

export const FONT = '"Pixelify Sans", "Silkscreen", monospace';
const shapes = new Map<string, HTMLCanvasElement>();

function bubbleShape(kind: 'say' | 'think', w: number, h: number): HTMLCanvasElement {
  const key = kind + w + 'x' + h;
  let img = shapes.get(key); if (img) return img;
  const W = w + 2, H = h + 8, cx = Math.floor(W / 2);
  const inside = (x: number, y: number): boolean => {
    if (kind === 'say') {
      if (x >= 1 && x <= W - 2 && y >= 1 && y <= h) { const c = (x === 1 || x === W - 2) && (y === 1 || y === h); return !c; }
      return y > h && y <= h + 3 && x >= cx - (h + 3 - y) && x <= cx - 1 + (y === h + 1 ? 1 : 0);
    }
    if (x >= 2 && x <= W - 3 && y >= 2 && y <= h - 1) return true;
    for (let bx = 3; bx <= W - 4; bx += 5) if (inEll(x, y, bx + 1, 2.5, 3, 2.4) || inEll(x, y, bx + 1, h - 1.5, 3, 2.4)) return true;
    if (inEll(x, y, 2.5, h / 2 + 0.5, 2.4, h / 2 - 1) || inEll(x, y, W - 2.5, h / 2 + 0.5, 2.4, h / 2 - 1)) return true;
    return inEll(x, y, cx - 2, h + 3, 2, 1.8) || inEll(x, y, cx - 4, h + 6.2, 1.2, 1.2);
  };
  img = sprite(W, H + 1, (x: number, y: number) => inside(x, y) ? (y === h - 1 && kind === 'say' ? '#efe8d8' : '#fffdf6') : null) as HTMLCanvasElement;
  shapes.set(key, img); return img;
}

export type BubbleContent = string | { icon: string };

/** A speech or thought bubble. `life` (0..1 through its time on screen) fades it in and out instead of popping. */
export function drawBubble(ctx: CanvasRenderingContext2D, x: number, footY: number, kind: 'say' | 'think', content: BubbleContent, camY: number, life = 0.5): void {
  const fade = Math.min(1, life / 0.06, (1 - life) / 0.1);
  if (fade <= 0) return;
  const prev = ctx.globalAlpha; ctx.globalAlpha = prev * fade;
  const top = Math.round(footY) - 34 + Math.round((1 - Math.min(1, life / 0.06)) * 3);
  ctx.font = '8px ' + FONT;
  let w: number, h = 11; let icon: { icon?: string[] } | undefined;
  const text = typeof content === 'string' ? (content.length > 28 ? content.slice(0, 27) + '…' : content) : '';
  if (typeof content === 'object') { icon = EMOTES.find((e: { id: string }) => e.id === content.icon); w = 11; h = 9; }
  else w = Math.ceil(ctx.measureText(text).width) + 8;
  const img = bubbleShape(kind, w, h);
  const bx = Math.round(x) - Math.floor(img.width / 2) + (kind === 'think' ? 3 : 0), by = Math.max(Math.round(camY) + 2, top - img.height + 1);
  ctx.drawImage(img, bx, by);
  if (icon?.icon) icon.icon.forEach((r: string, yy: number) => { for (let xx = 0; xx < r.length; xx++) if (r[xx] !== '.') { ctx.fillStyle = EMOTE_COLORS[r[xx]]; ctx.fillRect(bx + 4 + xx, by + 3 + yy, 1, 1); } });
  else { ctx.fillStyle = INK; ctx.fillText(text, bx + 5, by + 9); }
  ctx.globalAlpha = prev;
}

export function panel(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  const p = (xx: number, yy: number, ww: number, hh: number, c: string) => { ctx.fillStyle = c; ctx.fillRect(xx, yy, ww, hh); };
  p(x + 1, y + h, w - 2, 2, 'rgba(47,42,38,.35)');
  p(x + 1, y, w - 2, h, INK); p(x, y + 1, w, h - 2, INK);
  p(x + 1, y + 1, w - 2, h - 2, '#fffaf0'); p(x + 2, y + 2, w - 4, 1, '#ffffff'); p(x + 2, y + h - 3, w - 4, 1, '#efe2c8');
}

export function wrap(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const words = text.split(' '), lines: string[] = []; let cur = '';
  for (const w of words) { const t = cur ? cur + ' ' + w : w; if (ctx.measureText(t).width > maxW && cur) { lines.push(cur); cur = w; } else cur = t; }
  if (cur) lines.push(cur); return lines;
}

export interface DialogueView { name: string; right: boolean; portrait: HTMLCanvasElement; tagColor: string; text: string; done: boolean }

export function drawDialogue(ctx: CanvasRenderingContext2D, VW: number, VH: number, d: DialogueView, now: number): void {
  const h = 62, w = Math.min(VW - 12, 300), x = Math.floor((VW - w) / 2), y = Math.max(14, VH - h - 6);   // name tag needs 10 px above
  panel(ctx, x, y, w, h);
  const px = d.right ? x + w - 55 : x + 6, py = y + 7;
  ctx.fillStyle = INK; ctx.fillRect(px - 1, py - 1, 50, 50);
  ctx.drawImage(d.portrait, px, py, 48, 48);
  ctx.font = '8px "Silkscreen", monospace';
  const nw = Math.ceil(ctx.measureText(d.name).width) + 10, nx = d.right ? x + w - nw - 6 : x + 6, ny = y - 10;
  panel(ctx, nx, ny, nw, 12); ctx.fillStyle = d.tagColor; ctx.fillRect(nx + 2, ny + 2, 2, 8);
  ctx.fillStyle = INK; ctx.fillText(d.name, nx + 6, ny + 9);
  ctx.font = '9px ' + FONT; ctx.fillStyle = INK;
  const tx = d.right ? x + 8 : x + 60, tw = w - 70;
  wrap(ctx, d.text, tw).slice(0, 4).forEach((l, i) => ctx.fillText(l, tx, y + 15 + i * 11));
  if (d.done) { const ax = d.right ? x + w - 66 : x + w - 10, ay = y + h - 10 + (Math.floor(now / 400) % 2); /* a gentle bob, not a blink */ ctx.fillStyle = INK; ctx.fillRect(ax, ay, 5, 1); ctx.fillRect(ax + 1, ay + 1, 3, 1); ctx.fillRect(ax + 2, ay + 2, 1, 1); }
}

/** Small name tag above a head, with a faction-colored pip. */
export function drawName(ctx: CanvasRenderingContext2D, x: number, footY: number, name: string, color: string, self: boolean): void {
  ctx.font = '6px "Silkscreen", monospace';
  const w = Math.ceil(ctx.measureText(name).width), tx = Math.round(x - w / 2 + 2), ty = Math.round(footY) - 33;
  ctx.fillStyle = 'rgba(23,27,32,.55)'; ctx.fillRect(tx - 5, ty - 6, w + 8, 8);
  ctx.fillStyle = color; ctx.fillRect(tx - 4, ty - 4, 2, 4);
  ctx.fillStyle = self ? '#fff3c4' : '#ffffff'; ctx.fillText(name, tx, ty);
}
